"use client";

import React from "react";
import { Check, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const tabsData = [{
  id: "hotels",
  title: "Back-Office",
  heading: "Precision Data & Admin Support",
  description: "Optimize your internal workflows with our high-accuracy back-office solutions. Since 2017, we have refined our processes to ensure your administrative tasks are handled with technical precision and absolute reliability.",
  benefits: ["Data Entry & Management", "Document Processing", "Inventory Reconciliation", "Compliance Monitoring"],
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673581/img/bpo_back_office_precision.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673581/img/bpo_back_office_precision.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673581/img/bpo_back_office_precision.png-wvc-sizes",
    alt: "Back-office operational support"
  },
  ctaText: "Explore Back-Office",
  ctaLink: "/BpoServicesPage"
}, {
  id: "airlines",
  title: "Support",
  heading: "Omnichannel Customer Care",
  description: "Deliver exceptional brand experiences through our dedicated support teams. We combine human empathy with technical efficiency to resolve inquiries across voice, chat, and email platforms 24/7.",
  benefits: ["Multi-language Support", "Technical Troubleshooting", "SLA-Driven Performance", "Real-time Analytics"],
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673581/img/customer_support_excellence.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673581/img/customer_support_excellence.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673581/img/customer_support_excellence.png-wvc-sizes",
    alt: "Customer support center operations"
  },
  ctaText: "Explore Support",
  ctaLink: "/BpoServicesPage"
}, {
  id: "companies",
  title: "Training",
  heading: "Skills Development Academy",
  description: "Leverage our legacy in professional training to upskill your workforce. Our tailored programs bridge the gap between current capabilities and future industry demands through immersive learning.",
  benefits: ["Custom Curriculum Design", "Leadership Development", "Technical Skills Training", "Performance Coaching"],
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673581/img/training_academy_growth.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673581/img/training_academy_growth.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673581/img/training_academy_growth.png-wvc-sizes",
    alt: "Professional training and skills development"
  },
  ctaText: "Explore Academy",
  ctaLink: "/TrainingAcademyPage"
}];
export default function TailoredSolutions() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="245253" id="operational-excellence-hub" className="bg-background text-foreground py-24 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={headerRef} className={`max-w-3xl mx-auto text-center mb-16 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold tracking-tighter mb-6 text-foreground">
            Operational Excellence Hub
          </h2>
          <p className="font-default text-lg text-muted-foreground leading-relaxed">
            Integrated BPO and training solutions designed to scale your business operations with precision and professional expertise.
          </p>
        </div>

        <div ref={contentRef} className={`transition-all duration-1000 delay-200 ease-out ${isContentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Tabs defaultValue="hotels" className="w-full flex flex-col items-center">
            <TabsList className="mb-12 grid w-full max-w-md grid-cols-3 h-auto p-1 bg-muted/30 border border-border rounded-md">
              {tabsData.map((tab, index) => <TabsTrigger key={tab.id} value={tab.id} data-index={index} className="py-3 font-mono text-xs md:text-sm uppercase tracking-wider data-[state=active]:bg-primary data-[state=active]:text-primary-foreground transition-all duration-300 rounded-md">
                  {tab.title}
                </TabsTrigger>)}
            </TabsList>

            {tabsData.map((tab, index) => <TabsContent key={tab.id} value={tab.id} data-index={index} className="w-full mt-0 focus-visible:outline-none focus-visible:ring-0">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-center">
                  <div className="order-2 lg:order-1 space-y-8">
                    <div>
                      <h3 className="font-default text-2xl md:text-3xl font-bold mb-4 tracking-tight text-foreground">
                        {tab.heading}
                      </h3>
                      <p className="font-default text-muted-foreground leading-relaxed text-lg">
                        {tab.description}
                      </p>
                    </div>

                    <ul className="space-y-4">
                      {tab.benefits.map((benefit, bIndex) => <li key={bIndex} data-index={bIndex} className="flex items-start gap-3">
                          <div className="mt-1 bg-secondary/10 p-1 rounded-md shrink-0">
                            <Check className="w-4 h-4 text-secondary" />
                          </div>
                          <span className="font-default text-foreground/90 font-medium">{benefit}</span>
                        </li>)}
                    </ul>

                    <div className="pt-4">
                      <Button asChild size="lg" className="group bg-primary text-primary-foreground hover:shadow-lg hover:scale-105 transition-all duration-300 rounded-md">
                        <Link to={tab.ctaLink}>
                          {tab.ctaText}
                          <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                        </Link>
                      </Button>
                    </div>
                  </div>

                  <div className="order-1 lg:order-2 relative">
                    <div className="absolute inset-0 bg-secondary/10 rounded-lg transform translate-x-4 translate-y-4 -z-10 blur-xl"></div>
                    <div className="relative rounded-lg overflow-hidden border border-border shadow-xl aspect-[4/3] backdrop-blur-xl bg-card/40">
                      <img src={tab.image.src} data-wvc-srcset={tab.image.srcset} data-wvc-sizes={tab.image.sizes} alt={tab.image.alt} className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" loading="lazy" />
                    </div>
                  </div>
                </div>
              </TabsContent>)}
          </Tabs>
        </div>
      </div>
    </section>;
}